"use client";

import blocks from "@/components/page/blocks.module.css";
import { bpcComparison, bpcSource, decimal, thousands, type BpcRow } from "@/content/research-data";
import styles from "./charts.module.css";
import { useChartFrame } from "./useChartFrame";

export type BpcChartProps = {
  /** Filas por idioma (por defecto, la comparación real de research-data). */
  data?: BpcRow[];
  /** Versión corta: barras más bajas y pie más breve. */
  compact?: boolean;
  title?: string;
  hideTitle?: boolean;
  hideTable?: boolean;
  id?: string;
  className?: string;
};

const OURS = "YvyrAI Nano";
const THEIRS = "GPT-2 124M";

/**
 * Bits por carácter en español y en inglés, YvyrAI Nano contra GPT-2 124M (guía 11,
 * gráfico 3). Los dos lados se muestran: que GPT-2 gane en inglés es parte de la
 * honestidad del dato.
 */
export function BpcChart({
  data = bpcComparison,
  compact = false,
  title = "Cada modelo gana en su idioma.",
  hideTitle = false,
  hideTable = false,
  id = "grafico-bpc",
  className,
}: BpcChartProps) {
  const { ref, width: w, inView } = useChartFrame<HTMLDivElement>(640);

  const narrow = w < 520;
  const labelW = narrow ? 92 : 136;
  const valueW = 56;
  const barH = compact ? 14 : 18;
  const barGap = 8;
  const max = niceMax(Math.max(...data.flatMap((r) => [r.yvyrai, r.gpt2])));
  const ticks = range(0, max, narrow ? 1 : 0.5);
  const x0 = labelW;
  const pw = Math.max(1, w - labelW - valueW);
  const x = (v: number) => x0 + (v / max) * pw;

  // Alto de cada grupo: encabezado, dos barras y la nota de diferencia.
  const groupH = 24 + barH * 2 + barGap + 40;
  const top = 4;
  const axisY = top + groupH * data.length + 4;
  const h = axisY + 40;

  const summary = data
    .map((r) => `${r.language}: ${OURS} ${decimal(r.yvyrai, 4)}, ${THEIRS} ${decimal(r.gpt2, 4)}`)
    .join(". ");

  const titleId = `${id}-titulo`;

  return (
    <figure
      id={id}
      className={[styles.chart, className].filter(Boolean).join(" ")}
      data-compact={compact ? "true" : undefined}
      data-inview={inView ? "true" : undefined}
      aria-labelledby={titleId}
    >
      <div className={styles.header}>
        <p id={titleId} className={hideTitle ? "sr-only" : styles.title}>
          {title}
        </p>
        <ul className={styles.legend} aria-hidden="true">
          <li>
            <span className={styles.swatch} data-kind="bar" />
            {OURS}
          </li>
          <li>
            <span className={styles.swatch} data-kind="bar-muted" />
            {THEIRS}
          </li>
        </ul>
      </div>

      <div ref={ref} className={styles.plot}>
        <svg
          className={styles.svg}
          viewBox={`0 0 ${w} ${h}`}
          width={w}
          height={h}
          role="img"
          aria-label={`Bits por carácter, menos es mejor. ${summary}.`}
        >
          {data.map((r, gi) => {
            const gy = top + gi * groupH;
            // En celular la nota arranca en el borde izquierdo: así entra entera.
            const noteX = narrow ? 0 : x0;
            const oursWins = r.yvyrai < r.gpt2;
            const loser = oursWins ? THEIRS : OURS;
            const extra = ((Math.max(r.yvyrai, r.gpt2) / Math.min(r.yvyrai, r.gpt2) - 1) * 100).toFixed(1);
            const bars = [
              { key: "ours", label: OURS, value: r.yvyrai, wins: oursWins, cls: styles.barOurs },
              { key: "theirs", label: THEIRS, value: r.gpt2, wins: !oursWins, cls: styles.barTheirs },
            ];
            return (
              <g key={r.id}>
                {/* La grilla cruza solo las barras, no el encabezado ni la nota del grupo. */}
                {ticks.map((t) => (
                  <line
                    key={`g${t}`}
                    className={styles.grid}
                    x1={x(t)}
                    x2={x(t)}
                    y1={gy + 20}
                    y2={gy + 28 + barH * 2 + barGap}
                  />
                ))}
                <text className={styles.groupLabel} x={0} y={gy + 14}>
                  {r.language}
                  <tspan className={styles.groupMeta} dx={8}>
                    {r.corpus}
                  </tspan>
                </text>
                {bars.map((b, bi) => {
                  const by = gy + 24 + bi * (barH + barGap);
                  return (
                    <g key={b.key}>
                      <text className={styles.tick} x={0} y={by + barH / 2 + 4}>
                        {b.label}
                      </text>
                      <rect
                        className={`${b.cls} ${styles.grow}`}
                        data-order={gi * 2 + bi}
                        x={x0}
                        y={by}
                        width={Math.max(1, x(b.value) - x0)}
                        height={barH}
                      />
                      <text
                        className={styles.tick}
                        data-strong={b.wins ? "true" : undefined}
                        x={x(b.value) + 8}
                        y={by + barH / 2 + 4}
                      >
                        {decimal(b.value, 4)}
                      </text>
                    </g>
                  );
                })}
                <text className={`${styles.note} ${styles.fade}`} x={noteX} y={gy + 24 + barH * 2 + barGap + 20}>
                  {loser} necesita {extra.replace(".", ",")} % más bits
                </text>
              </g>
            );
          })}

          <line className={styles.axis} x1={x0} x2={x(max)} y1={axisY} y2={axisY} />
          {ticks.map((t) => (
            <text key={`t${t}`} className={styles.tick} x={x(t)} y={axisY + 16} textAnchor="middle">
              {decimal(t, Number.isInteger(t) ? 0 : 1)}
            </text>
          ))}
          <text className={styles.axisTitle} x={narrow ? 0 : x0} y={h - 4}>
            bits por carácter · menos es mejor
          </text>
        </svg>
      </div>

      <figcaption className={styles.fine}>
        <span>
          Bits por carácter: cuánta información necesita el modelo para predecir cada letra. Se usa en lugar de la
          perplejidad porque los dos modelos tienen tokenizers distintos.
        </span>
        <span>
          Mismo texto para los dos modelos: {data.map((r) => `${thousands(r.chars)} caracteres en ${r.language.toLowerCase()}`).join(" y ")}.
        </span>
        {!compact && (
          <span>
            Esto no prueba que la arquitectura sea superior: ganar en español es especialización de idioma, con corpus y
            tokenizer dedicados.
          </span>
        )}
        <span>Fuente: {bpcSource.file}.</span>
      </figcaption>

      {!hideTable && (
        <details className={styles.data}>
          <summary>Ver los datos</summary>
          <div className={`${styles.dataBody} ${blocks.tableWrap}`}>
            <table className={blocks.table}>
              <caption>Bits por carácter sobre el mismo texto (menos es mejor)</caption>
              <thead>
                <tr>
                  <th scope="col">Conjunto</th>
                  <th scope="col" className={blocks.num}>
                    {OURS}
                  </th>
                  <th scope="col" className={blocks.num}>
                    {THEIRS}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.map((r) => (
                  <tr key={r.id}>
                    <th scope="row">
                      {r.language} ({r.corpus})
                    </th>
                    <td className={blocks.num}>{decimal(r.yvyrai, 4)}</td>
                    <td className={blocks.num}>{decimal(r.gpt2, 4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </figure>
  );
}

function niceMax(v: number) {
  return Math.ceil(v * 2) / 2;
}

function range(from: number, to: number, step: number) {
  const out: number[] = [];
  for (let v = from; v <= to + 1e-9; v += step) out.push(Math.round(v * 10) / 10);
  return out;
}
